import { getServiceWorldCaseLink } from "./Product/ProductService/productService";
import { CalendlyEvent } from "../types/Calendly/CalendlyEvent";
import { Customer, CustomerConsent } from "../types/Customer";
import { ProductUnion } from "../types/ProductUnion";
import { Activity } from "../types/Activity";

const EXTERNAL_CALENDLY_URL = "https://calendly.com";

export const getCalendlyPhoneCallIds = (event: CalendlyEvent): number[] =>
  [...new Set([...(event.phoneCallIds ?? []), ...(event.phoneCallId ? [event.phoneCallId] : [])])];

export const getCalendlyEventReference = (calendlyEvent: Activity["calendlyEvent"]): string | null =>
  typeof calendlyEvent === "string" ? calendlyEvent : (calendlyEvent?.["@id"] ?? null);

const getProductCalendlyLink = (product: ProductUnion) => {
  return getServiceWorldCaseLink(product) + "/calendly";
};

export const buildCalendlyLinkLawyer = (product: ProductUnion, isExternal = false) => {
  if (import.meta.env.VITE_DEPLOYMENT !== "production") {
    return combineCalendlyLinkLawyerParts(product, "dev", isExternal);
  }

  if (!product.backofficeCase.lawyer) {
    return combineCalendlyLinkLawyerParts(product, "frank-riedel", isExternal);
  }

  const lawyerSlug =
    product.backofficeCase.lawyer.person.givenName.replace(/\s/, "").toLowerCase() +
    "-" +
    product.backofficeCase.lawyer.person.familyName.replace(/\s/, "").toLowerCase();
  return combineCalendlyLinkLawyerParts(product, lawyerSlug, isExternal);
};

const combineCalendlyLinkLawyerParts = (product: ProductUnion, lawyerSlug: string, isExternal = false) => {
  return (
    (isExternal ? EXTERNAL_CALENDLY_URL : getProductCalendlyLink(product)) +
    `/legalbird-${lawyerSlug}/beratungsgespraech?utm_source=` +
    encodeURI(btoa(String(product.backofficeCaseLb.id)))
  );
};

const isValidDateString = (s: unknown): s is string =>
  typeof s === "string" && s.trim() !== "" && !Number.isNaN(Date.parse(s));

export function getUserConsent(customer: Customer, type: string): CustomerConsent | null {
  return (customer?.consents ?? []).find((c) => c?.type === type) ?? null;
}

export const hasUserConsent = (customer: Customer, type: string): boolean => {
  return (customer?.consents ?? []).some(
    (c) =>
      c?.type === type &&
      isValidDateString(c?.approvalDate) &&
      (c?.denialDate == null || String(c.denialDate).trim() === "")
  );
};

export const hasLegacyUserConsent = (calendlyEvent: CalendlyEvent, type: string) => {
  if (!calendlyEvent.consentGiver) {
    return false;
  }

  if (calendlyEvent.consentDenialDate) {
    return false;
  }

  return !!(calendlyEvent.consentApprovalDate && calendlyEvent.consentType === type);
};

export const buildCalendlyLink = (product: ProductUnion, isExternal = false) => {
  let linkExtension = "/legalbird-frank-riedel/beratungsgespraech";

  if (import.meta.env.VITE_DEPLOYMENT !== "production") {
    linkExtension = "/legalbird-dev/beratungsgespraech";
    return (
      (isExternal ? EXTERNAL_CALENDLY_URL : getProductCalendlyLink(product)) +
      linkExtension +
      "?utm_source=" +
      encodeURI(btoa(String(product.backofficeCaseLb.id)))
    );
  }

  switch (product.productClassName) {
    case "death":
      linkExtension = "/legalbird/erstberatung-todesfall-erbrecht";
      break;
    case "alimony":
      linkExtension = "/legalbird/erstberatung-unterhalt";
      break;
    case "familyLawCase":
      linkExtension = "/legalbird/erstberatung-familienrecht";
      break;
    case "divorce":
      linkExtension = "/legalbird/erstberatung-trennung-scheidung";
      break;
    case "eventContract":
    case "membershipContract":
    case "otherContract":
    case "providerContract":
    case "dataLeakContract":
    case "purchaseContract":
    case "serviceContract":
    case "subscriptionContract":
    case "travelContract":
    case "vehicleContract":
      linkExtension = "/legalbird/erstberatung-vertragsrecht";
      break;
    default:
      return buildCalendlyLinkLawyer(product, isExternal);
  }
  return (
    (isExternal ? EXTERNAL_CALENDLY_URL : getProductCalendlyLink(product)) +
    linkExtension +
    "?utm_source=" +
    encodeURI(btoa(String(product.backofficeCaseLb.id)))
  );
};
